import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';
import supabase from '../lib/supabase';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiMail, FiPhone, FiBriefcase, FiX, FiCheck, FiAlertCircle } = FiIcons;

const MemberManagement = ({ onNavigate }) => {
  const { members, addMember, updateMember, deleteMember, loadAllData } = useAttendance();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.name || !formData.email) {
        setFormError('Name and email are required');
        setIsSubmitting(false);
        return;
      }
      
      console.log('Submitting member data:', formData);
      
      if (editingMember) {
        // Update existing member
        await updateMember(editingMember.id, formData);
        setEditingMember(null);
      } else {
        // Add new member - direct database approach
        const { error } = await supabase
          .from('members_moco_png')
          .insert({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            company: formData.company || null,
            active: true,
            consecutiveAbsences: 0
            // joinDate has been completely removed
          });
          
        if (error) {
          throw new Error(`Database error: ${error.message}`);
        }
        
        // Reload data to refresh the members list
        await loadAllData();
      }

      // Reset form
      setFormData({ name: '', email: '', phone: '', company: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error submitting member:', error);
      setFormError(error.message || 'Failed to save member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      company: member.company || ''
    });
    setFormError('');
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingMember(null);
    setFormData({ name: '', email: '', phone: '', company: '' });
    setFormError('');
  };

  const handleDelete = async (id) => {
    try {
      await deleteMember(id);
    } catch (error) {
      console.error('Error deleting member:', error);
      // Consider adding a toast notification here
    }
  };

  const activeMembers = members.filter(m => m.active);
  const inactiveMembers = members.filter(m => !m.active);

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex justify-between items-center"
      >
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Member Management</h2>
          <p className="text-gray-600 mt-2">
            Manage your networking group members
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary flex items-center"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Add Member
        </button>
      </motion.div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-xl font-semibold">
                {editingMember ? 'Edit Member' : 'Add New Member'}
              </h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg border border-red-200 flex items-start">
                    <SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>
                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex items-center"
                  >
                    {isSubmitting ? (
                      <span className="inline-block animate-spin mr-2">⟳</span>
                    ) : (
                      <SafeIcon icon={FiCheck} className="mr-2" />
                    )}
                    {editingMember ? 'Update Member' : 'Add Member'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center"
                  >
                    <SafeIcon icon={FiX} className="mr-2" /> Cancel
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold">
              Active Members ({activeMembers.length})
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {activeMembers.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.email}</p>
                      {member.company && (
                        <p className="text-sm text-gray-500">{member.company}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </motion.div>
              ))}
              {activeMembers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiUser} className="mx-auto text-4xl mb-4" />
                  <p>No active members yet</p>
                  <p className="text-sm">Add your first member to get started</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold">
              Removed Members ({inactiveMembers.length})
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {inactiveMembers.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-red-50 rounded-lg opacity-75"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-400 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.email}</p>
                      {member.removedDate && (
                        <p className="text-sm text-red-600">
                          Removed: {new Date(member.removedDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {inactiveMembers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No removed members</p>
                  <p className="text-sm">Members who miss meetings will appear here</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MemberManagement;